import React, { useEffect, useRef, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { FiChevronDown } from "react-icons/fi";
import MessageBubble from "./MessageBubble";
import ImageLightbox from "./ImageLightbox";
import { fetchMessages, setHighlightMessage } from "../../../Slices/chatSlice";
import "./chatWidget.css";

const MessageList = ({ conversationId }) => {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const msgState = useSelector((state) => state.chat.messages[conversationId]);
  const messages = msgState?.items || [];
  const hasMore = msgState?.hasMore || false;
  const loading = msgState?.loading || false;
  const page = msgState?.page || 1;
  const typingUsers = useSelector(
    (state) => state.chat.typingUsers[conversationId] || {}
  );
  const highlightMessageId = useSelector((state) => state.chat.highlightMessageId);
  const findInChatQuery = useSelector((state) => state.chat.findInChatQuery);

  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState("");
  const [newMsgCount, setNewMsgCount] = useState(0);
  const [isScrolledUp, setIsScrolledUp] = useState(false);

  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isInitialLoad = useRef(true);
  const prevMsgCountRef = useRef(0);
  const messageRefs = useRef({});

  // Reset new message count when conversation changes
  useEffect(() => {
    setNewMsgCount(0);
    setIsScrolledUp(false);
  }, [conversationId]);

  // Load initial messages
  useEffect(() => {
    if (conversationId) {
      isInitialLoad.current = true;
      dispatch(fetchMessages({ conversationId, page: 1 }));
    }
  }, [conversationId, dispatch]);

  // Auto-scroll to bottom on new messages or initial load
  useEffect(() => {
    if (!containerRef.current) return;
    if (isInitialLoad.current && messages.length > 0) {
      bottomRef.current?.scrollIntoView();
      isInitialLoad.current = false;
      prevMsgCountRef.current = messages.length;
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
    if (isNearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      setNewMsgCount(0);
    } else if (messages.length > prevMsgCountRef.current && prevMsgCountRef.current > 0) {
      // User is scrolled up and new messages arrived
      const newCount = messages.length - prevMsgCountRef.current;
      setNewMsgCount((prev) => prev + newCount);
    }
    prevMsgCountRef.current = messages.length;
  }, [messages.length]);

  // Scroll to highlighted search result
  useEffect(() => {
    if (highlightMessageId && messageRefs.current[highlightMessageId]) {
      messageRefs.current[highlightMessageId].scrollIntoView({ behavior: "smooth", block: "center" });
      // Clear highlight after animation
      const timer = setTimeout(() => dispatch(setHighlightMessage(null)), 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightMessageId, dispatch]);

  // Restore scroll position after loading older messages
  useEffect(() => {
    if (page > 1 && containerRef.current && prevScrollHeightRef.current) {
      const newScrollHeight = containerRef.current.scrollHeight;
      containerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
    }
  }, [messages.length, page]);

  // Infinite scroll up + track scroll position
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 150;
    setIsScrolledUp(!nearBottom);
    if (nearBottom) setNewMsgCount(0);

    if (!loading && hasMore && scrollTop < 100) {
      prevScrollHeightRef.current = containerRef.current.scrollHeight;
      dispatch(fetchMessages({ conversationId, page: page + 1 }));
    }
  }, [loading, hasMore, conversationId, page, dispatch]);

  const jumpToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setNewMsgCount(0);
    setIsScrolledUp(false);
  };

  const getDateLabel = (dateStr) => {
    const date = dayjs(dateStr);
    const today = dayjs();
    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
    return date.format("MMM D, YYYY");
  };

  const handleImageClick = (src, alt) => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
  };

  const typers = Object.values(typingUsers);
  let lastDate = null;

  // Find first unread message index (messages not read by current user)
  const firstUnreadIdx = messages.findIndex(
    (msg) =>
      msg.senderId !== loginUser?.id &&
      !(msg.readBy || []).some((r) => r.userId === loginUser?.id)
  );
  const unreadCount = firstUnreadIdx >= 0 ? messages.length - firstUnreadIdx : 0;

  return (
    <>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="chat-messages"
      >
        {loading && page > 1 && (
          <div style={{ textAlign: "center", padding: 8 }}>
            <div className="chat-typing-dots">
              <span /><span /><span />
            </div>
          </div>
        )}

        {messages.map((msg, idx) => {
          const msgDate = dayjs(msg.createdAt).format("YYYY-MM-DD");
          let showDate = false;
          if (msgDate !== lastDate) {
            showDate = true;
            lastDate = msgDate;
          }
          // Show sender name if different from previous message sender
          const prevMsg = idx > 0 ? messages[idx - 1] : null;
          const showSender =
            msg.type !== "system" &&
            msg.senderId !== loginUser?.id &&
            (!prevMsg || prevMsg.senderId !== msg.senderId || prevMsg.type === "system" || showDate);

          return (
            <React.Fragment key={msg.id || msg.tempId || idx}>
              {showDate && (
                <div className="chat-messages__date-divider">
                  <span className="chat-messages__date-pill">
                    {getDateLabel(msg.createdAt)}
                  </span>
                </div>
              )}
              {idx === firstUnreadIdx && unreadCount > 0 && (
                <div className="chat-messages__unread-divider">
                  <div className="chat-messages__unread-line" />
                  <span className="chat-messages__unread-label">
                    {unreadCount} new message{unreadCount > 1 ? "s" : ""}
                  </span>
                  <div className="chat-messages__unread-line" />
                </div>
              )}
              <div ref={(el) => { if (msg.id) messageRefs.current[msg.id] = el; }}>
                <MessageBubble
                  message={msg}
                  isOwn={msg.senderId === loginUser?.id}
                  showSender={showSender}
                  onImageClick={handleImageClick}
                  conversationId={conversationId}
                  isHighlighted={msg.id === highlightMessageId}
                  searchQuery={findInChatQuery}
                />
              </div>
            </React.Fragment>
          );
        })}

        {loading && page === 1 && messages.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--chat-text-muted)" }}>
            <div className="chat-typing-dots" style={{ justifyContent: "center" }}>
              <span /><span /><span />
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {typers.length > 0 && (
          <div className="chat-messages__typing">
            <div className="chat-typing-dots">
              <span /><span /><span />
            </div>
            <span className="chat-messages__typing-text">
              {typers.length === 1
                ? `${typers[0].userName} is typing`
                : typers.length === 2
                ? `${typers[0].userName} and ${typers[1].userName} are typing`
                : "Several people are typing"}
            </span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Jump to new messages button */}
      {(isScrolledUp || newMsgCount > 0) && (
        <button className="chat-messages__jump-btn" onClick={jumpToBottom}>
          <FiChevronDown size={16} />
          {newMsgCount > 0 ? (
            <span>{newMsgCount} new message{newMsgCount > 1 ? "s" : ""}</span>
          ) : (
            <span>Jump to latest</span>
          )}
        </button>
      )}

      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc}
          alt={lightboxAlt}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </>
  );
};

export default MessageList;
